import React, { useEffect, useState } from 'react'
import {
  Avatar,
  Typography,
  Box,
  LinearProgress
} from '@mui/material'
import { useGlobalStoreData } from '../../store/globalStore'
import Content from '../../layout/content/Content'
import Head from '../../layout/head/Head'
import { useTranslation } from 'react-i18next'
import { MarketCalculusCard } from '../../components/preview/Preview'
import Grid from '@mui/material/Grid'
import '../../mailTemplate/src/styles/globals.scss'
import 'flag-icons/css/flag-icons.min.css'
import SimplePieChart from '../../components/amcharts/SimplePieChart'
import McPageHelpers from '../mc-components/McPageHelpers'
import { getBooleanUserRolesByUser } from '../../mailTemplate/src/utils/helpers'

const UserProfile = () => {
  const { t } = useTranslation()
  const user = useGlobalStoreData.user()
  const userRoles = getBooleanUserRolesByUser(user)
  const countries = useGlobalStoreData.countries()

  const [limits, setLimits] = useState([])

  useEffect(() => {
    if (user.limit) {
      setLimits(McPageHelpers.createLimitsArray(user.limit, userRoles.B2B))
    }
  }, [user, userRoles.B2B])

  const getUserCountryName = () => {
    return countries.find((item) => item.countryCode === user.countryCode)?.countryName || t('unknown')
  }

  const getLocationString = () => {
    const city = user.city || ''
    const country = getUserCountryName() || ''
    return [city, country].filter(Boolean).join(', ')
  }

  return (
    <React.Fragment>
      <Head title={t('menu.user')}/>
      <Content>
        <Grid container spacing="1rem">
          <Grid item xs={12} sm={6} md={3}>
            <MarketCalculusCard>
              <div className="flex flex-col justify-around md:flex-row gap-8 items-center md:items-start m-4">
                <Box className="flex flex-col items-center">
                  <Avatar
                    alt={user.nameSurname || user.userName}
                    src={user.avatar || '/default-avatar.png'}
                    sx={{ width: 100, height: 100, boxShadow: '0 4px 6px rgba(0,0,0,0.1)', marginBottom: '1rem' }}
                  />
                  <Typography variant="h5" fontWeight="bold">
                    {user.nameSurname || user.userName}
                  </Typography>
                  <Typography variant="subtitle1" color="textSecondary">
                    {user.roles?.join(', ')}
                  </Typography>
                  <Typography variant="body1" color="textPrimary" className="mt-2">
                    {user.companyName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" className="mt-1">
                    {getLocationString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" className="mt-1">
                    {user.email}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" className="mt-1">
                    {user.tel}
                  </Typography>
                  {user.openDate && <Typography variant="body2" color="textSecondary" className="mt-1">
                    {t('mailTemplate.users.openDate')}: {new Date(user.openDate).toLocaleDateString()}
                  </Typography>}
                  {user.closeDate && <Typography variant="body2" color="textSecondary" className="mt-1">
                    {t('mailTemplate.users.closeDate')}: {new Date(user.closeDate).toLocaleDateString()}
                  </Typography>}
                  {user.closeDate && <Typography variant="body2" color="textSecondary" className="mt-1">
                    {t('mailTemplate.users.closeDate')}: {new Date(user.closeDate).toLocaleDateString()}
                  </Typography>}
                  {user?.roles && (
                    <Typography variant="body2" color="textSecondary" className="mt-1">
                      {t('limit.allowedCountries')}:{' '}
                      {(McPageHelpers.getAllowedSearchCountries(user) || [])
                        .map(
                          country =>
                            countries.find(item => item.countryCode === country)?.countryName || t('unknown')
                        )
                        .join(', ')}
                    </Typography>
                  )}
                </Box>
              </div>
            </MarketCalculusCard>
          </Grid>
          <Grid item xs={12} sm={6} md={9}>
            <Grid container spacing={2} className="mb-3">
              {limits.map((limit, index) => (
                <Grid item xs={12} sm={6} md={4} key={limit.key}>
                  <MarketCalculusCard>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <Typography color="text.primary" className="font-medium">
                          {t(limit.label)}
                        </Typography>
                        <Typography color="text.primary" className="font-medium">
                          {limit.used}/{limit.total}
                        </Typography>
                      </div>
                      <LinearProgress variant="determinate"
                                      value={(limit.used / limit.total) * 100}/>
                      <Typography variant="body2">
                        {limit.total - limit.used < 0 ? 0 : limit.total - limit.used} ({t('limit.balance')})
                      </Typography>
                    </div>
                    <SimplePieChart chartId={`myPieChart${index}`} data={limit}/>
                  </MarketCalculusCard>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Content>
    </React.Fragment>
  )
}

export default UserProfile
